import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { OAuthButtons } from './OAuthButtons';
import { Brand } from '../common/Brand';

const PHONE_REGEX = /^[\d\s+\-().]{7,30}$/;

export const RegisterForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkEmail, setCheckEmail] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmedName = name.trim();
    const trimmedPhone = phone.trim();

    if (trimmedName.length < 2) {
      setError('Please enter your full name (at least 2 characters)');
      return;
    }

    if (trimmedPhone && !PHONE_REGEX.test(trimmedPhone)) {
      setError('Phone must be 7-30 chars: digits, spaces, +, -, (, ), .');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const res = await register({
        email,
        password,
        name: trimmedName,
        phone: trimmedPhone || undefined,
      });
      if (res.verificationRequired) {
        // Account created, but the user must verify their email before signing in.
        setCheckEmail(true);
        return;
      }
      // Verification off: account is live on the Free plan — go to the dashboard.
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (checkEmail) {
    return (
      <div className="auth auth-center">
        <div className="auth-card">
          <div className="auth-brand"><Brand variant="full" height={64} /></div>
          <h1 className="auth-title">Check your email</h1>
          <p className="auth-sub">
            We sent a verification link to <b style={{ color: 'var(--ivory)' }}>{email}</b>. Click it
            to activate your account, then sign in.
          </p>
          <p className="auth-note">
            Didn't get it? Check your spam folder, or{' '}
            <button
              type="button"
              onClick={() => setCheckEmail(false)}
              className="auth-btn auth-btn-ghost"
              style={{ display: 'inline', width: 'auto', height: 'auto', padding: 0, border: 'none', color: 'var(--champ)' }}
            >
              try again
            </button>
            .
          </p>
          <p className="auth-note"><Link to="/login">Go to sign in →</Link></p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth auth-center">
      <div className="auth-card">
        <div className="auth-brand"><Brand variant="full" height={64} /></div>
        <h1 className="auth-title">Create your account</h1>
        <p className="auth-sub">Already have an account? <Link to="/login">Sign in</Link></p>
        <p className="auth-note" style={{ marginTop: 0, marginBottom: 26 }}>
          Free forever — all features, up to 2 devices. No card required. Upgrade to Pro anytime.
        </p>
        <form onSubmit={handleSubmit}>
          {error && <div className="auth-error" role="alert">{error}</div>}

          <div className="auth-field">
            <label className="auth-label" htmlFor="name">Full name</label>
            <input
              id="name" name="name" type="text" autoComplete="name" required
              className="auth-input" placeholder="Jane Doe"
              value={name} onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="auth-field">
            <label className="auth-label" htmlFor="email">Email address</label>
            <input
              id="email" name="email" type="email" autoComplete="email" required
              className="auth-input" placeholder="you@example.com"
              value={email} onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="auth-field">
            <label className="auth-label" htmlFor="phone">
              Phone number <span style={{ color: 'var(--faint)' }}>(optional)</span>
            </label>
            <input
              id="phone" name="phone" type="tel" autoComplete="tel"
              className="auth-input" placeholder="+1 (555) 123-4567"
              value={phone} onChange={(e) => setPhone(e.target.value)}
            />
            <p className="auth-note" style={{ textAlign: 'left', marginTop: 7 }}>
              We'll only use this for important account or security notices.
            </p>
          </div>

          <div className="auth-field">
            <label className="auth-label" htmlFor="password">Password</label>
            <input
              id="password" name="password" type="password" autoComplete="new-password" required
              className="auth-input" placeholder="At least 8 characters"
              value={password} onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <div className="auth-field">
            <label className="auth-label" htmlFor="confirm-password">Confirm password</label>
            <input
              id="confirm-password" name="confirm-password" type="password" autoComplete="new-password" required
              className="auth-input" placeholder="Repeat your password"
              value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>

          <button type="submit" disabled={loading} className="auth-btn" style={{ marginTop: 6 }}>
            {loading ? <LoadingSpinner size="sm" /> : 'Create account'}
          </button>

          <OAuthButtons />
        </form>
      </div>
    </div>
  );
};
